import { useState } from 'react';
import type { Ticket, TicketMessage } from '@shared/types';
import { useAuth } from '../app/auth';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime, formatRelative } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  Notice,
  PageHeader,
  Pagination,
  Select,
  Table,
  Textarea,
  type Column,
} from '../components/ui';

interface TicketList {
  items: Ticket[];
  total: number;
  page: number;
  pageSize: number;
}

interface TicketDetail {
  ticket: Ticket;
  messages: TicketMessage[];
}

const STATUS_LABELS: Record<Ticket['status'], string> = {
  open: '处理中',
  pending: '等待客户',
  resolved: '已解决',
  closed: '已关闭',
};

const STATUS_TONES = {
  open: 'brand',
  pending: 'warn',
  resolved: 'ok',
  closed: 'neutral',
} as const;

const CATEGORY_LABELS: Record<string, string> = {
  billing: '计费',
  technical: '技术',
  account: '账号',
  other: '其他',
};

export function TicketsPage() {
  const { can } = useAuth();
  const canReply = can('ticket.reply');

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('open');
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [version, setVersion] = useState(0);

  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [nextStatus, setNextStatus] = useState<'open' | 'pending' | 'resolved' | 'closed'>('pending');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tickets = useAsync<TicketList>(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: '20' });
    if (status !== '') params.set('status', status);
    if (category !== '') params.set('category', category);
    if (appliedQuery !== '') params.set('query', appliedQuery);
    return api.get(`/api/tickets?${params.toString()}`);
  }, [page, status, category, appliedQuery, version]);

  async function openDetail(ticket: Ticket) {
    setLoadingId(ticket.id);
    setError(null);
    setReply('');
    setNextStatus(ticket.status === 'open' ? 'pending' : ticket.status);
    try {
      setDetail(await api.get<TicketDetail>(`/api/tickets/${ticket.id}`));
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setLoadingId(null);
    }
  }

  async function sendReply() {
    if (!detail) return;
    if (reply.trim() === '') {
      setError('回复内容不能为空');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await api.post<TicketDetail>(`/api/tickets/${detail.ticket.id}/messages`, {
        body: reply.trim(),
        status: nextStatus,
      });
      setDetail(next);
      setReply('');
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(next: 'open' | 'pending' | 'resolved' | 'closed') {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/tickets/${detail.ticket.id}/status`, { status: next });
      setDetail(await api.get<TicketDetail>(`/api/tickets/${detail.ticket.id}`));
      setNextStatus(next);
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<Ticket>[] = [
    {
      key: 'subject',
      header: '工单',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-fg">{row.subject}</p>
          <p className="text-xs text-fg-subtle">
            {row.tenantName} · {CATEGORY_LABELS[row.category] ?? row.category} · {row.messageCount} 条消息
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: '状态',
      render: (row) => <Badge tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status]}</Badge>,
    },
    {
      key: 'priority',
      header: '优先级',
      render: (row) => <span className="text-xs text-fg-muted">{row.priority}</span>,
    },
    {
      key: 'updated',
      header: '更新',
      render: (row) => <span className="text-xs text-fg-muted">{formatRelative(row.updatedAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '90px',
      render: (row) => (
        <Button size="sm" loading={loadingId === row.id} onClick={() => void openDetail(row)}>
          处理
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="工单"
        description="回复会自动发邮件通知提交人；状态为「已关闭」的工单客户不能继续回复。"
      />

      {error && detail === null ? <Notice tone="danger">{error}</Notice> : null}

      <Card>
        <CardHeader title="工单队列" hint={`${tickets.data?.total ?? 0} 条`} />
        <div className="mb-3 flex flex-wrap gap-2">
          <div className="w-36">
            <Select
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              options={[
                { value: 'open', label: '处理中' },
                { value: '', label: '全部状态' },
                { value: 'pending', label: '等待客户' },
                { value: 'resolved', label: '已解决' },
                { value: 'closed', label: '已关闭' },
              ]}
            />
          </div>
          <div className="w-32">
            <Select
              value={category}
              onChange={(value) => {
                setCategory(value);
                setPage(1);
              }}
              options={[
                { value: '', label: '全部类型' },
                { value: 'billing', label: '计费' },
                { value: 'technical', label: '技术' },
                { value: 'account', label: '账号' },
                { value: 'other', label: '其他' },
              ]}
            />
          </div>
          <div className="w-64">
            <Input
              value={query}
              onChange={setQuery}
              placeholder="标题 / 工单号 / 组织"
              onEnter={() => {
                setAppliedQuery(query.trim());
                setPage(1);
              }}
            />
          </div>
          <Button
            onClick={() => {
              setAppliedQuery(query.trim());
              setPage(1);
            }}
          >
            搜索
          </Button>
        </div>

        <Table
          columns={columns}
          rows={tickets.data?.items ?? []}
          getKey={(row) => row.id}
          loading={tickets.loading}
          empty="没有符合条件的工单"
        />
        <Pagination
          page={page}
          pageSize={tickets.data?.pageSize ?? 20}
          total={tickets.data?.total ?? 0}
          onChange={setPage}
        />
      </Card>

      <Modal
        open={detail !== null}
        title={detail ? detail.ticket.subject : '工单'}
        description={detail ? `${detail.ticket.tenantName} · ${STATUS_LABELS[detail.ticket.status]}` : undefined}
        onClose={() => setDetail(null)}
        width="max-w-2xl"
        footer={<Button onClick={() => setDetail(null)}>关闭</Button>}
      >
        {detail ? (
          <div className="space-y-4">
            {error ? <Notice tone="danger">{error}</Notice> : null}

            <div className="space-y-2">
              {detail.messages.map((message) => (
                <div
                  key={message.id}
                  className={`rounded-md px-3 py-2 text-[13px] ${
                    message.authorType === 'admin' ? 'bg-brand/5' : 'bg-canvas'
                  }`}
                >
                  <p className="mb-1 flex items-center justify-between text-xs text-fg-muted">
                    <span>
                      {message.authorName}
                      {message.authorType === 'admin' ? '（我方）' : ''}
                    </span>
                    <span>{formatDateTime(message.createdAt)}</span>
                  </p>
                  <p className="whitespace-pre-wrap text-fg">{message.body}</p>
                </div>
              ))}
            </div>

            {canReply ? (
              <div className="space-y-2 border-t border-line pt-3">
                <Textarea
                  value={reply}
                  onChange={setReply}
                  rows={3}
                  maxLength={5000}
                  placeholder="回复客户…"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-40">
                    <Select
                      value={nextStatus}
                      onChange={setNextStatus}
                      options={[
                        { value: 'pending', label: '回复后：等待客户' },
                        { value: 'open', label: '回复后：处理中' },
                        { value: 'resolved', label: '回复后：标记已解决' },
                        { value: 'closed', label: '回复后：关闭' },
                      ]}
                    />
                  </div>
                  <Button variant="primary" loading={busy} onClick={() => void sendReply()}>
                    发送回复
                  </Button>
                  <Button loading={busy} onClick={() => void changeStatus('closed')}>
                    直接关闭
                  </Button>
                </div>
              </div>
            ) : (
              <Notice tone="info">当前角色只能查看工单。</Notice>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
